import type {
    AvatarShape,
    AvatarTone,
    AvatarVariant,
} from "@design-system/Avatar/Avatar";
import {
    avatarShapes,
    avatarTones,
    avatarVariants,
} from "./AvatarPlayground.lib";
import type { AnatomyBinding } from "./componentAnatomy";

export interface AvatarPaint {
    shape: AvatarShape;
    variant: AvatarVariant;
    tone: AvatarTone;
}

const paintPartId = (shape: AvatarShape) =>
    shape === "diamond" ? "fill" : "background";

const neutralFillBindings = (shape: AvatarShape): readonly AnatomyBinding[] => [
    {
        partId: paintPartId(shape),
        property: "background-color",
        token: "--bg-tertiary",
        via: "--avatar-bg",
        note: 'Set by [data-tone="neutral"]; opaque, so stacks stay clean. Neutral avatars take no `color` (type union).',
    },
];

const outlineBindings = ({
    shape,
    tone,
}: Omit<AvatarPaint, "variant">): readonly AnatomyBinding[] => [
    {
        partId: paintPartId(shape),
        property: "background-color",
        token: "--bg-primary",
        via: "--avatar-bg",
    },
    {
        partId: "border",
        property: "box-shadow width",
        token: "--border-width",
        note:
            shape === "diamond"
                ? "inset 0 0 0 on the rotated ::before; the root's shadow is removed."
                : "inset 0 0 0 on the root, inside the edge.",
    },
    tone === "neutral"
        ? {
              partId: "border",
              property: "box-shadow color",
              token: "--border-default",
              via: "--avatar-ring",
          }
        : {
              partId: "border",
              property: "box-shadow color",
              token: "--color-ocean-blue-200",
              via: "--avatar-ring",
              note: 'Set to --avatar-color by [data-variant="outline"][data-tone="colorful"], so a `color` prop paints the ring.',
          },
];

/** The fill and ring bindings one variant and tone add on top of a shape's resting bindings. */
export const avatarPaintBindings = ({
    shape,
    variant,
    tone,
}: AvatarPaint): readonly AnatomyBinding[] => {
    if (variant === "outline") {
        return outlineBindings({ shape, tone });
    }
    return tone === "neutral" ? neutralFillBindings(shape) : [];
};

/** The anatomy variant id for a playground shape, variant and tone, e.g. `diamond-outline-neutral`. */
export const avatarAnatomyVariantId = ({ shape, variant, tone }: AvatarPaint) =>
    `${shape}-${variant}-${tone}`;

export const avatarPaints: readonly AvatarPaint[] = avatarShapes.flatMap(
    (shape) =>
        avatarVariants.flatMap((variant) =>
            avatarTones.map((tone) => ({ shape, variant, tone })),
        ),
);

/** Splits an anatomy variant id back into the playground's toggles; unknown ids read as a solid colorful circle. */
export const parseAvatarAnatomyVariantId = (variantId: string): AvatarPaint =>
    avatarPaints.find(
        (paint) => avatarAnatomyVariantId(paint) === variantId,
    ) ?? {
        shape: "circle",
        variant: "solid",
        tone: "colorful",
    };
